import React, { Dispatch, SetStateAction, useState } from 'react';
import { useFormikContext } from 'formik';
import {
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    styled,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { ConfirmationDialog } from '@fnol/library/src/components';
import { legalPersonType, personType, translations } from '../../lib/consts';
import { IndividualCleanup } from '../Individual/Individual';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const createData = (name: string, relation: string, editable: boolean, editId: number) => {
    return { name, relation, editable, editId };
};

type GenericObject = { [key: string]: any };

interface InjuredPersonsTableProps {
    inputPersonOpen: boolean;
    setInputPersonOpen: Dispatch<SetStateAction<boolean>>;
    setInputPersonId: Dispatch<SetStateAction<number>>;
    setAddPossible: Dispatch<SetStateAction<boolean>>;
}

const InjuredPersonsTable = ({
    inputPersonOpen,
    setInputPersonOpen,
    setInputPersonId,
    setAddPossible,
}: InjuredPersonsTableProps) => {
    const { values, setFieldValue } = useFormikContext<GenericObject>();
    const editIds = [1, 2, 3, 4, 5];

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(0);

    const rows: GenericObject[] = [];

    const copyInjuredPersonToId = (idFrom: number, idTo: number) => {
        setFieldValue(`injured${idTo}IndividualName`, values[`injured${idFrom}IndividualName`]);
        setFieldValue(`injured${idTo}IndividualLastName`, values[`injured${idFrom}IndividualLastName`]);
        setFieldValue(`injured${idTo}IndividualUCN`, values[`injured${idFrom}IndividualUCN`]);
        setFieldValue(`injured${idTo}IndividualCountry`, values[`injured${idFrom}IndividualCountry`]);
        setFieldValue(`injured${idTo}IndividualMyCountry`, values[`injured${idFrom}IndividualMyCountry`]);
        setFieldValue(`injured${idTo}IndividualCity`, values[`injured${idFrom}IndividualCity`]);
        setFieldValue(`injured${idTo}IndividualCityInSerbia`, values[`injured${idFrom}IndividualCityInSerbia`]);
        setFieldValue(`injured${idTo}IndividualStreet`, values[`injured${idFrom}IndividualStreet`]);
        setFieldValue(`injured${idTo}IndividualPhone`, values[`injured${idFrom}IndividualPhone`]);
        setFieldValue(`injured${idTo}IndividualEmail`, values[`injured${idFrom}IndividualEmail`]);
        setFieldValue(
            `injured${idTo}IndividualSubmitterRelationId`,
            values[`injured${idFrom}IndividualSubmitterRelationId`],
        );
        setFieldValue(
            `injured${idTo}IndividualReportingImmaterialDamage`,
            values[`injured${idFrom}IndividualReportingImmaterialDamage`],
        );
        setFieldValue(`injured${idTo}IndividualPersonRoleId`, values[`injured${idFrom}IndividualPersonRoleId`]);
        setFieldValue(
            `injured${idTo}IndividualImmaterialDamageDescription`,
            values[`injured${idFrom}IndividualImmaterialDamageDescription`],
        );
    };

    const handleEditInjuredPerson = (editId: number) => {
        setFieldValue('injuredIndividualName', values[`injured${editId}IndividualName`]);
        setFieldValue('injuredIndividualLastName', values[`injured${editId}IndividualLastName`]);
        setFieldValue('injuredIndividualUCN', values[`injured${editId}IndividualUCN`]);
        setFieldValue('injuredIndividualCountry', values[`injured${editId}IndividualCountry`]);
        setFieldValue('injuredIndividualMyCountry', values[`injured${editId}IndividualMyCountry`]);
        setFieldValue('injuredIndividualCity', values[`injured${editId}IndividualCity`]);
        setFieldValue('injuredIndividualCityInSerbia', values[`injured${editId}IndividualCityInSerbia`]);
        setFieldValue('injuredIndividualStreet', values[`injured${editId}IndividualStreet`]);
        setFieldValue('injuredIndividualPhone', values[`injured${editId}IndividualPhone`]);
        setFieldValue('injuredIndividualEmail', values[`injured${editId}IndividualEmail`]);
        setFieldValue('injuredIndividualSubmitterRelationId', values[`injured${editId}IndividualSubmitterRelationId`]);
        setFieldValue(
            'injuredIndividualReportingImmaterialDamage',
            values[`injured${editId}IndividualReportingImmaterialDamage`],
        );
        setFieldValue('injuredIndividualPersonRoleId', values[`injured${editId}IndividualPersonRoleId`]);
        setFieldValue(
            'injuredIndividualImmaterialDamageDescription',
            values[`injured${editId}IndividualImmaterialDamageDescription`],
        );
        setInputPersonId(editId);
        setInputPersonOpen(true);
    };

    const handleDeleteInjuredPerson = (deleteId: number) => {
        editIds.map((editId) => {
            if (editId === deleteId) {
                IndividualCleanup(personType.injured, setFieldValue, deleteId);
                setAddPossible(true);
                let counter = editId;
                while (counter <= editIds.length) {
                    const oneUp = counter + 1;
                    if (values[`injured${oneUp}IndividualName`]) {
                        copyInjuredPersonToId(oneUp, counter);
                        IndividualCleanup(personType.injured, setFieldValue, oneUp);
                    }
                    counter++;
                }
            }
        });
    };

    const filterRoleById = (roles: GenericObject[], id: number) => {
        const filteredRoles = roles.filter((role: GenericObject) => role.id === id);
        return filteredRoles[0]['text'];
    };

    if (
        values.submitterType === legalPersonType.individual &&
        values.submitterIndividualReportingImmaterialDamage === 'true'
    ) {
        rows.push(
            createData(
                `${values.submitterIndividualName} ${values.submitterIndividualLastName}`,
                filterRoleById(values.submitterPersonRoles, values.submitterIndividualPersonRoleId),
                false,
                0,
            ),
        );
    }
    if (
        values.ownerType &&
        values.ownerType === legalPersonType.individual &&
        values.ownerIndividualReportingImmaterialDamage === 'true'
    ) {
        rows.push(
            createData(
                `${values.ownerIndividualName} ${values.ownerIndividualLastName}`,
                filterRoleById(values.ownerPersonRoles, values.ownerIndividualPersonRoleId),
                false,
                0,
            ),
        );
    }
    if (values.driverType && values.driverIndividualReportingImmaterialDamage === 'true') {
        rows.push(
            createData(
                `${values.driverIndividualName} ${values.driverIndividualLastName}`,
                filterRoleById(values.driverPersonRoles, values.driverIndividualPersonRoleId),
                false,
                0,
            ),
        );
    }
    if (
        values.userType &&
        values.userType === legalPersonType.individual &&
        values.userIndividualReportingImmaterialDamage === 'true'
    ) {
        rows.push(
            createData(
                `${values.userIndividualName} ${values.userIndividualLastName}`,
                filterRoleById(values.userPersonRoles, values.userIndividualPersonRoleId),
                false,
                0,
            ),
        );
    }
    if (
        values.ownerBuildingType &&
        values.ownerBuildingType === legalPersonType.individual &&
        values.ownerBuildingIndividualReportingImmaterialDamage === 'true'
    ) {
        rows.push(
            createData(
                `${values.ownerBuildingIndividualName} ${values.ownerBuildingIndividualLastName}`,
                filterRoleById(values.ownerBuildingPersonRoles, values.ownerBuildingIndividualPersonRoleId),
                false,
                0,
            ),
        );
    }
    editIds.map((editId) => {
        if (values[`injured${editId}IndividualName`]) {
            rows.push(
                createData(
                    `${values[`injured${editId}IndividualName`]} ${values[`injured${editId}IndividualLastName`]}`,
                    filterRoleById(values.injuredPersonRoles, values[`injured${editId}IndividualPersonRoleId`]),
                    true,
                    editId,
                ),
            );
        }
    });

    return (
        <>
            <TableContainer component={Paper}>
                {rows.length > 0 && (
                    <Table sx={{ minWidth: 230 }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>{translations.PERSON}</StyledTableCell>
                                <StyledTableCell align="right">{translations.ROLE}</StyledTableCell>
                                <StyledTableCell align="right"> </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <StyledTableRow key={row.name}>
                                    <StyledTableCell component="th" scope="row">
                                        {row.name}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">{row.relation}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        {row.editable && (
                                            <>
                                                <IconButton
                                                    onClick={() => handleEditInjuredPerson(row.editId)}
                                                    aria-label="edit"
                                                    disabled={inputPersonOpen}
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => {
                                                        setDeleteId(row.editId);
                                                        setConfirmOpen(true);
                                                    }}
                                                    aria-label="delete"
                                                    disabled={inputPersonOpen}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <ConfirmationDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={() => handleDeleteInjuredPerson(deleteId)}
            >
                {translations.CONFIRM_DELETE}
            </ConfirmationDialog>
        </>
    );
};

export default InjuredPersonsTable;
